import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BarChart2, ArrowLeft, Shield, Database,
  AlertTriangle, ChevronDown, ChevronUp, CheckCircle2,
} from 'lucide-react';
import {
  getImplementationRegistry,
  getPhaseStatusColor,
  getPhaseStatusLabel,
  getIntegrationStatusLabel,
  getIntegrationStatusColor,
  getPriorityColor,
  getRiskImpactColor,
} from '../../lib/implementationRegistry';
import type { ImplRiskProbability, ImplRiskImpact } from '../../types';

const TABS = ['Timeline', 'Integrations', 'Risks'] as const;
type Tab = typeof TABS[number];

function probabilityDot(p: ImplRiskProbability) {
  const map: Record<ImplRiskProbability, string> = {
    high: 'bg-red-500',
    medium: 'bg-yellow-500',
    low: 'bg-green-500',
  };
  return map[p];
}

function impactLabel(i: ImplRiskImpact) {
  const map: Record<ImplRiskImpact, string> = {
    critical: 'Critical',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
  };
  return map[i];
}

export default function ImplementationTracker() {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const [activeTab, setActiveTab] = useState<Tab>('Timeline');
  const [expandedRisk, setExpandedRisk] = useState<string | null>(null);

  const registry = getImplementationRegistry();
  const { phases, integrations, risks, workstreams } = registry;

  const totalWeeks = phases.filter((p) => p.meta.durationWeeks > 0).reduce((s, p) => s + p.meta.durationWeeks, 0);
  const criticalIntegrations = integrations.filter((i) => i.priority === 'critical').length;
  const cdpRequired = integrations.filter((i) => i.cdpGoldenRecordRequired).length;
  const criticalRisks = risks.filter((r) => r.impact === 'critical').length;

  return (
    <div className={`max-w-7xl mx-auto px-4 py-8 ${isAr ? 'rtl' : 'ltr'}`} dir={isAr ? 'rtl' : 'ltr'}>

      {/* Header */}
      <div className="mb-8">
        <Link to="/implementation" className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-indigo-600 mb-4 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Implementation Guide
        </Link>
        <div className="flex items-start gap-4">
          <div className="p-3 bg-indigo-600 rounded-xl">
            <BarChart2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isAr ? 'متتبع البرنامج' : 'Programme Tracker'}
            </h1>
            <p className="text-gray-500 text-sm mt-0.5">
              {isAr ? 'الجدول الزمني، سجل التكاملات، وسجل المخاطر' : 'Timeline, integration register, and risk register'}
            </p>
          </div>
        </div>
      </div>

      {/* Summary bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        {[
          { label: 'Total Phases', value: phases.length, color: 'text-indigo-600' },
          { label: 'Programme Weeks', value: `${totalWeeks}+`, color: 'text-blue-600' },
          { label: 'Critical Integrations', value: criticalIntegrations, color: 'text-rose-600' },
          { label: 'Critical Risks', value: criticalRisks, color: 'text-amber-600' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm text-center">
            <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6 w-fit">
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab}
            {tab === 'Integrations' && (
              <span className="ml-1.5 text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full">{integrations.length}</span>
            )}
            {tab === 'Risks' && (
              <span className="ml-1.5 text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full">{risks.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── TIMELINE TAB ── */}
      {activeTab === 'Timeline' && (
        <div className="space-y-3">
          {phases.map((phase) => {
            const statusColor = getPhaseStatusColor(phase.meta.status);
            const statusLabel = getPhaseStatusLabel(phase.meta.status, isAr);
            const barWidth = phase.meta.durationWeeks > 0
              ? `${Math.min(100, (phase.meta.durationWeeks / Math.max(totalWeeks, 1)) * 100 * 2.5)}%`
              : '10%';

            return (
              <Link
                key={phase.meta.id}
                to={`/implementation/phase/${phase.meta.id}`}
                className="block bg-white rounded-2xl border border-gray-200 hover:border-indigo-300 hover:shadow-sm transition-all p-5 group"
              >
                <div className="flex items-center gap-4">
                  {/* Phase number */}
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                    style={{ backgroundColor: phase.meta.color }}
                  >
                    {phase.meta.number}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-semibold text-gray-900 text-sm group-hover:text-indigo-700 transition-colors">
                        {isAr ? phase.meta.titleAr : phase.meta.titleEn}
                      </span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColor}`}>
                        {statusLabel}
                      </span>
                    </div>

                    <div className="flex items-center gap-3 text-xs text-gray-400 mb-2">
                      {phase.meta.startDate && <span>{phase.meta.startDate}{phase.meta.endDate && ` → ${phase.meta.endDate}`}</span>}
                      {phase.meta.durationWeeks > 0 && <span>{phase.meta.durationWeeks} weeks</span>}
                    </div>

                    {/* Timeline bar */}
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div
                        className="h-2 rounded-full opacity-80"
                        style={{ width: barWidth, backgroundColor: phase.meta.color }}
                      />
                    </div>
                  </div>

                  {/* Workstream dots */}
                  <div className="hidden sm:flex gap-1 flex-shrink-0">
                    {phase.meta.activeWorkstreams.slice(0, 5).map((wsId) => {
                      const ws = workstreams.find((w) => w.id === wsId);
                      return ws ? (
                        <div
                          key={wsId}
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: ws.color }}
                          title={ws.label}
                        />
                      ) : null;
                    })}
                  </div>

                  {/* Milestones count */}
                  {phase.milestones.length > 0 && (
                    <div className="text-xs text-gray-400 flex-shrink-0 hidden md:block">
                      {phase.milestones.length} milestone{phase.milestones.length !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>

                {/* Inline milestones */}
                {phase.milestones.length > 0 && (
                  <div className="mt-3 pl-14 flex flex-wrap gap-2">
                    {phase.milestones.map((m, i) => (
                      <span key={i} className="text-xs bg-gray-50 border border-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                        {m.title}{m.date ? ` — ${m.date}` : ''}
                      </span>
                    ))}
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      )}

      {/* ── INTEGRATIONS TAB ── */}
      {activeTab === 'Integrations' && (
        <div>
          <div className="flex items-center gap-3 mb-4 p-4 bg-purple-50 border border-purple-200 rounded-xl">
            <Database className="w-5 h-5 text-purple-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-purple-800">CDP Golden Record Rule</p>
              <p className="text-xs text-purple-700 mt-0.5">
                {cdpRequired} of {integrations.length} integrations require the CDP Golden Record ID in every payload. This field is mandatory — integrations missing it are rejected at design review.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {integrations.map((intg) => (
              <div key={intg.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-semibold text-gray-900 text-sm">{intg.name}</span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getIntegrationStatusColor(intg.status)}`}>
                        {getIntegrationStatusLabel(intg.status)}
                      </span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getPriorityColor(intg.priority)}`}>
                        {intg.priority}
                      </span>
                      {intg.cdpGoldenRecordRequired && (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 flex items-center gap-1">
                          <Database className="w-3 h-3" /> CDP ID required
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                      <span className="bg-gray-100 px-2 py-0.5 rounded font-mono">{intg.source}</span>
                      <span className="text-gray-300">→</span>
                      <span className="bg-gray-100 px-2 py-0.5 rounded font-mono">{intg.target}</span>
                      <span className="text-gray-400">· {intg.direction} · due {intg.phaseDue}</span>
                    </div>

                    <p className="text-sm text-gray-600 leading-relaxed">{intg.description}</p>

                    {intg.errorHandling && (
                      <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
                        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                        {intg.errorHandling}
                      </div>
                    )}
                  </div>

                  <span className="text-xs font-semibold text-gray-400 flex-shrink-0 uppercase">{intg.id}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── RISKS TAB ── */}
      {activeTab === 'Risks' && (
        <div>
          <div className="flex items-center gap-3 mb-4 p-4 bg-red-50 border border-red-200 rounded-xl">
            <Shield className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800">
              <span className="font-semibold">{criticalRisks} critical-impact risks</span> in the programme register.
              Reviewed weekly by the Cross-Functional Task Force. Critical risks escalated to Steering Committee within 24 hours.
            </p>
          </div>

          <div className="space-y-3">
            {risks.map((risk) => {
              const isExpanded = expandedRisk === risk.id;
              const impactStyle = getRiskImpactColor(risk.impact);

              return (
                <div
                  key={risk.id}
                  className={`bg-white rounded-2xl border-2 shadow-sm transition-all ${impactStyle}`}
                >
                  <button
                    className="w-full p-5 text-left flex items-start gap-4"
                    onClick={() => setExpandedRisk(isExpanded ? null : risk.id)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-semibold text-gray-900 text-sm">{risk.title}</span>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${getRiskImpactColor(risk.impact)}`}>
                          {impactLabel(risk.impact)} Impact
                        </span>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                          {risk.category}
                        </span>
                      </div>

                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1.5">
                          <span className={`w-2 h-2 rounded-full ${probabilityDot(risk.probability)}`} />
                          {risk.probability} probability
                        </span>
                        <span>Owner: {risk.owner}</span>
                      </div>
                    </div>

                    {isExpanded
                      ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />
                      : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />
                    }
                  </button>

                  {isExpanded && (
                    <div className="px-5 pb-5 border-t border-current border-opacity-20">
                      <p className="text-sm text-gray-700 leading-relaxed mt-4 mb-4">{risk.description}</p>

                      {risk.mitigation.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Mitigations</h4>
                          <ul className="space-y-1.5">
                            {risk.mitigation.map((m, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                                <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                {m}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {risk.escalateTo && (
                        <div className="mt-4 text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
                          <span className="font-semibold">Escalate to:</span> {risk.escalateTo}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
